export interface KeyBinding {
  key: string;
  mod?: boolean;
  shift?: boolean | "optional";
  alt?: boolean | "optional";
}

export interface ShortcutAction {
  id: string;
  label: string;
  shortcuts: KeyBinding[];
  isEnabled?: () => boolean;
  run: (event: KeyboardEvent) => void;
}

export function findActionForKeyboardEvent(actions: ShortcutAction[], event: KeyboardEvent): ShortcutAction | undefined {
  return actions.find(action => {
    if (action.isEnabled?.() === false) {
      return false;
    }

    return action.shortcuts.some(shortcut => keyBindingMatchesEvent(shortcut, event));
  });
}

export function keyBindingMatchesEvent(binding: KeyBinding, event: KeyboardEvent): boolean {
  return (
    keyMatches(binding.key, event) &&
    (binding.mod ?? false) === (event.metaKey || event.ctrlKey) &&
    shiftMatches(binding.shift, event.shiftKey) &&
    modifierMatches(binding.alt, event.altKey)
  );
}

export function isEditableShortcutTarget(target: EventTarget | null): boolean {
  const element = resolveShortcutTargetElement(target);
  if (!element) {
    return false;
  }

  const tagName = element.tagName.toLowerCase();
  if (element.isContentEditable || tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  // Monaco focuses a hidden textarea, but some keys can target overlay/chrome nodes
  // inside `.monaco-editor`. Treat the whole editor surface as editable.
  return typeof element.closest === "function"
    && Boolean(element.closest(".monaco-editor, [data-curva-script-editor]"));
}

function resolveShortcutTargetElement(target: EventTarget | null): HTMLElement | undefined {
  if (isHtmlElementLike(target)) {
    return target;
  }
  if (typeof document !== "undefined" && isHtmlElementLike(document.activeElement)) {
    return document.activeElement;
  }
  return undefined;
}

function isHtmlElementLike(value: unknown): value is HTMLElement {
  return typeof value === "object"
    && value !== null
    && "tagName" in value
    && typeof (value as { tagName: unknown }).tagName === "string";
}

function shiftMatches(bindingShift: KeyBinding["shift"], eventShift: boolean): boolean {
  return modifierMatches(bindingShift, eventShift);
}

function modifierMatches(binding: boolean | "optional" | undefined, pressed: boolean): boolean {
  if (binding === "optional") {
    return true;
  }

  return (binding ?? false) === pressed;
}

function normalizeKey(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key.toLowerCase();
}

function keyMatches(bindingKey: string, event: KeyboardEvent): boolean {
  const normalizedBinding = normalizeKey(bindingKey);
  if (normalizedBinding === normalizeKey(event.key)) {
    return true;
  }

  if (/^[a-z]$/.test(normalizedBinding)) {
    return event.code.toLowerCase() === `key${normalizedBinding}`;
  }

  if (normalizedBinding === "\\") {
    return event.code.toLowerCase() === "backslash";
  }

  if (normalizedBinding === "~" || normalizedBinding === "`") {
    return event.code.toLowerCase() === "backquote";
  }

  return false;
}
